import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Paper,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import api, { API_BASE_URL } from "../../src/utils/api";

const MembersGridPage = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [viewMode, setViewMode] = useState("table");
  const [cardVisibleCount, setCardVisibleCount] = useState(12);

  const limit = useMemo(() => {
    if (viewMode === "cards") return isXs ? 24 : 48;
    return 100;
  }, [viewMode, isXs]);

  useEffect(() => {
    if (isXs) setViewMode("cards");
    else setViewMode("table");
  }, [isXs]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => setCurrentPage(1), [debouncedSearch, limit]);

  useEffect(() => {
    setCardVisibleCount(isXs ? 12 : 24);
  }, [isXs, viewMode, currentPage, debouncedSearch]);

  useEffect(() => {
    fetchMembers(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch, limit]);

  const fetchMembers = async (page, search, { force = false } = {}) => {
    setLoading(true);

    const cacheKey = `members-page-${page}-limit-${limit}-search-${search}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (!force && cachedData) {
      const { members, totalRecords } = JSON.parse(cachedData);
      setMembers(members || []);
      setTotalRecords(totalRecords || 0);
      setLoading(false);
      return;
    }

    if (force) sessionStorage.removeItem(cacheKey);

    try {
      const response = await api.get("/membersfilter", {
        params: { page, limit, search },
        headers: force ? { "Cache-Control": "no-cache" } : undefined,
      });

      const data = response.data || {};
      setMembers(data.members || []);
      setTotalRecords(data.totalRecords || 0);

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          members: data.members || [],
          totalRecords: data.totalRecords || 0,
        })
      );
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrent = () => {
    fetchMembers(currentPage, debouncedSearch, { force: true });
  };

  const clearAllMembersCache = () => {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("members-page-"))
      .forEach((k) => sessionStorage.removeItem(k));

    fetchMembers(currentPage, debouncedSearch, { force: true });
  };

  const displayedRows = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) return members;

    return members.filter((m) => {
      const fields = [
        m.CompanyCode,
        m.ClubMemberCode,
        m.LastName,
        m.FirstName,
        m.ClubMembershipTypeCode,
        m.ClubStatusRuleCode,
        m.EmailAddress,
        m.FullAddress,
      ];
      return fields.some((val) => String(val || "").toLowerCase().includes(q));
    });
  }, [members, debouncedSearch]);

  const cardsRows = useMemo(() => {
    if (viewMode !== "cards") return displayedRows;
    return displayedRows.slice(0, cardVisibleCount);
  }, [displayedRows, viewMode, cardVisibleCount]);

  const columnVisibilityModel = useMemo(() => {
    if (isXs) {
      return {
        CompanyCode: false,
        PhotoGraphicFileName: false,
        ClubMembershipTypeCode: false,
        ClubStatusRuleCode: false,
        EmailAddress: true,
        FullAddress: true,
        LastName: true,
        FirstName: true,
        ClubMemberCode: true,
      };
    }
    if (isSmDown) {
      return {
        CompanyCode: false,
        PhotoGraphicFileName: true,
        ClubMembershipTypeCode: true,
        ClubStatusRuleCode: false,
        EmailAddress: true,
        FullAddress: true,
        LastName: true,
        FirstName: true,
        ClubMemberCode: true,
      };
    }
    return {
      CompanyCode: true,
      PhotoGraphicFileName: true,
      ClubMembershipTypeCode: true,
      ClubStatusRuleCode: true,
      EmailAddress: true,
      FullAddress: true,
      LastName: true,
      FirstName: true,
      ClubMemberCode: true,
    };
  }, [isXs, isSmDown]);

  const columns = useMemo(
    () => [
      { field: "CompanyCode", headerName: "Company Code", flex: 1, minWidth: 140 },
      {
        field: "ClubMemberCode",
        headerName: "Club Member Code",
        flex: 1,
        minWidth: 170,
        renderCell: (params) => (
          <Button
            component={RouterLink}
            to={`/home/clubmembers/${params.row.ClubMemberCode}`}
            variant="text"
            size="small"
            sx={{
              color: "#2f63ad",
              fontWeight: 700,
              textTransform: "none",
              px: 0,
              minWidth: 0,
            }}
          >
            {params.value?.trim()}
          </Button>
        ),
      },
      {
        field: "PhotoGraphicFileName",
        headerName: "Photo",
        flex: 0.7,
        minWidth: 110,
        sortable: false,
        renderCell: (params) => (
          <img
            loading="lazy"
            src={`${API_BASE_URL}/images/${encodeURIComponent(
              params.row?.PhotoGraphicFileName?.trim() || ""
            )}`}
            alt="Member"
            onError={(e) => (e.currentTarget.src = "/img/100x100.png")}
            style={{
              width: 42,
              height: 42,
              borderRadius: "999px",
              objectFit: "cover",
            }}
          />
        ),
      },
      { field: "LastName", headerName: "Last Name", flex: 1, minWidth: 140 },
      { field: "FirstName", headerName: "First Name", flex: 1, minWidth: 140 },
      {
        field: "ClubMembershipTypeCode",
        headerName: "Membership Type",
        flex: 1,
        minWidth: 170,
      },
      {
        field: "ClubStatusRuleCode",
        headerName: "Club Status Rule",
        flex: 1,
        minWidth: 170,
      },
      {
        field: "EmailAddress",
        headerName: "Email",
        flex: 1.4,
        minWidth: 220,
        renderCell: (params) => {
          const email = params.value?.trim();
          return email ? (
            <Tooltip title={`Send email to ${email}`}>
              <Box
                component="a"
                href={`mailto:${email}`}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#2f63ad",
                  textDecoration: "none",
                  minWidth: 0,
                }}
              >
                <EmailIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {email}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            "N/A"
          );
        },
      },
      {
        field: "FullAddress",
        headerName: "Address",
        flex: 2,
        minWidth: 280,
        renderCell: (params) => {
          const address = params.value?.trim();
          const mapsLink = address
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
            : null;

          return address ? (
            <Tooltip title={`View ${address} on Google Maps`}>
              <Box
                component="a"
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#2f63ad",
                  textDecoration: "none",
                  minWidth: 0,
                }}
              >
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {address}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            "N/A"
          );
        },
      },
    ],
    []
  );

  const gridMinWidth = useMemo(() => {
    const minWidthMap = {
      CompanyCode: 140,
      ClubMemberCode: 170,
      PhotoGraphicFileName: 110,
      LastName: 140,
      FirstName: 140,
      ClubMembershipTypeCode: 170,
      ClubStatusRuleCode: 170,
      EmailAddress: 220,
      FullAddress: 280,
    };

    const visibleFields = Object.entries(columnVisibilityModel)
      .filter(([, visible]) => visible)
      .map(([field]) => field);

    const total = visibleFields.reduce(
      (sum, field) => sum + (minWidthMap[field] || 140),
      0
    );

    return Math.max(520, total + 40);
  }, [columnVisibilityModel]);

  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / limit));

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 2.25,
    boxShadow: "none",
    backgroundColor: "#2f63ad",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
    "&:active": { transform: "translateY(1px)" },
    "&:disabled": { backgroundColor: "#90caf9", color: "#fff" },
    whiteSpace: "nowrap",
  };

  const secondaryButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
    "&:active": { transform: "translateY(1px)" },
  };

  const MemberCard = ({ m }) => {
    const email = m.EmailAddress?.trim();
    const address = m.FullAddress?.trim();
    const mapsLink = address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null;

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          backgroundColor: "rgba(255,255,255,0.96)",
          overflow: "hidden",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 12px 28px rgba(0,0,0,0.1)",
            transform: "translateY(-1px)",
          },
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <img
              loading="lazy"
              src={`${API_BASE_URL}/images/${encodeURIComponent(
                m?.PhotoGraphicFileName?.trim() || ""
              )}`}
              alt="Member"
              onError={(e) => (e.currentTarget.src = "/img/100x100.png")}
              style={{
                width: 48,
                height: 48,
                borderRadius: "999px",
                flex: "0 0 auto",
                objectFit: "cover",
                border: "1px solid rgba(47,99,173,0.08)",
              }}
            />

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 800, color: "#223142" }} noWrap>
                {m.LastName?.trim() || "—"}, {m.FirstName?.trim() || "—"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 0.75,
                }}
              >
                <Chip
                  size="small"
                  label={m.ClubMemberCode?.trim() || "No Code"}
                  sx={{ fontWeight: 700 }}
                />
                {m.CompanyCode?.trim() ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={m.CompanyCode.trim()}
                    sx={{ fontWeight: 600 }}
                  />
                ) : null}
                {m.ClubMembershipTypeCode?.trim() ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={m.ClubMembershipTypeCode.trim()}
                    sx={{ fontWeight: 600 }}
                  />
                ) : null}
              </Box>
            </Box>

            <Button
              component={RouterLink}
              to={`/home/clubmembers/${m.ClubMemberCode}`}
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              sx={{ ...secondaryButtonSx, fontWeight: 800 }}
            >
              Open
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Stack spacing={1.1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, width: 90, color: "text.secondary", flexShrink: 0 }}
              >
                Status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {m.ClubStatusRuleCode?.trim() || "—"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, width: 90, color: "text.secondary", flexShrink: 0 }}
              >
                Email
              </Typography>

              {email ? (
                <Box
                  component="a"
                  href={`mailto:${email}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "#2f63ad",
                    textDecoration: "none",
                    minWidth: 0,
                  }}
                >
                  <EmailIcon fontSize="small" />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {email}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">N/A</Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, width: 90, color: "text.secondary", flexShrink: 0 }}
              >
                Address
              </Typography>

              {address ? (
                <Box
                  component="a"
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "#2f63ad",
                    textDecoration: "none",
                    minWidth: 0,
                  }}
                >
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {address}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">N/A</Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(47,99,173,0.08)",
            }}
          >
            <GroupIcon sx={{ color: "#2f63ad" }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
            }}
          >
            Club Members
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Search, browse, and open member profiles in table or card view.
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ ...pageCardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, member code, address..."
            size="small"
            fullWidth
            sx={{
              ...fieldSx,
              maxWidth: { xs: "100%", md: 760 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={refreshCurrent}
              disabled={loading}
              startIcon={<RefreshIcon />}
              sx={primaryButtonSx}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>

            <Button
              variant="outlined"
              onClick={clearAllMembersCache}
              disabled={loading}
              startIcon={<ClearAllIcon />}
              sx={secondaryButtonSx}
            >
              Clear Cache
            </Button>

            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 1.5,
                  borderColor: "rgba(47,99,173,0.18)",
                  color: "#223142",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(47,99,173,0.08)",
                    color: "#2f63ad",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <ViewListIcon fontSize="small" sx={{ mr: 0.75 }} />
                Table
              </ToggleButton>
              <ToggleButton value="cards">
                <ViewModuleIcon fontSize="small" sx={{ mr: 0.75 }} />
                Cards
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
          Showing <strong>{displayedRows.length}</strong> of{" "}
          <strong>{totalRecords || members.length}</strong> members
        </Typography>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...sectionCardSx,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 280,
            }}
          >
            <CircularProgress />
          </Box>
        ) : displayedRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <GroupIcon sx={{ fontSize: 42, color: "#9aa6b2", mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              No members found
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
              Try updating your search to find matching club members.
            </Typography>
          </Box>
        ) : (
          <>
            {viewMode === "cards" ? (
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 1.25,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr 1fr",
                    },
                  }}
                >
                  {cardsRows.map((m) => (
                    <MemberCard key={m.ClubMemberCode} m={m} />
                  ))}
                </Box>

                {displayedRows.length > cardsRows.length && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setCardVisibleCount((c) =>
                          Math.min(displayedRows.length, c + (isXs ? 12 : 24))
                        )
                      }
                      sx={{ ...secondaryButtonSx, fontWeight: 800 }}
                    >
                      Load more
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {isXs && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      pt: 1,
                      color: "text.secondary",
                      display: "block",
                    }}
                  >
                    Swipe left/right to see more columns →
                  </Typography>
                )}

                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    display: "block",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: gridMinWidth,
                      width: "max-content",
                      maxWidth: "100%",
                    }}
                  >
                    <DataGrid
                      rows={displayedRows}
                      columns={columns}
                      getRowId={(row) => row.ClubMemberCode}
                      autoHeight
                      disableRowSelectionOnClick
                      hideFooter
                      rowHeight={58}
                      columnVisibilityModel={columnVisibilityModel}
                      sx={{
                        border: "none",
                        minWidth: gridMinWidth,
                        width: "100%",
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "rgba(47,99,173,0.04)",
                          borderBottom: "1px solid rgba(47,99,173,0.08)",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: 800,
                          color: "#223142",
                        },
                        "& .MuiDataGrid-cell": {
                          borderBottom: "1px solid rgba(47,99,173,0.05)",
                          display: "flex",
                          alignItems: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#223142",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          display: "none",
                        },
                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: "rgba(47,99,173,0.03)",
                        },
                        "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
                          px: 2,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
                minWidth: 0,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  disabled={loading || currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  sx={secondaryButtonSx}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  disabled={loading || currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  sx={secondaryButtonSx}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MembersGridPage;
